import {useMemo} from 'react'
import {type AppBskyFeedDefs} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {useAgent} from '#/state/session'

const RQKEY_ROOT = 'classroom-posts'
export const RQKEY = (uris: string[]) => [RQKEY_ROOT, uris.join(' ')]

/** How many posts one `getPosts` call may ask for. */
const BATCH_SIZE = 25

/**
 * How long to wait before asking a second time for a URI that did not come back.
 *
 * A post written a moment ago is the one case where "not here yet" is not the
 * same as "gone": a read that races the write comes back empty, and telling the
 * person who just published a capsule from inside a classroom that their capsule
 * is no longer here would be the app's mistake, not theirs.
 */
const SECOND_CHANCE_MS = 1500

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/** One `getPosts` per batch, answering with whatever came back. */
async function fetchPosts(
  agent: ReturnType<typeof useAgent>,
  uris: string[],
): Promise<AppBskyFeedDefs.PostView[]> {
  const posts: AppBskyFeedDefs.PostView[] = []
  for (let i = 0; i < uris.length; i += BATCH_SIZE) {
    const batch = uris.slice(i, i + BATCH_SIZE)
    try {
      const res = await agent.getPosts({uris: batch})
      posts.push(...res.data.posts)
    } catch {
      // One bad batch must not blank the whole page.
    }
  }
  return posts
}

/**
 * The real posts a classroom's entries point at.
 *
 * A classroom does not hold copies of work: it holds references to the posts
 * its members published, and this is where those references become posts
 * again. They come back in one request per batch rather than one per entry,
 * because a classroom's page shows a whole list at once.
 *
 * A URI that does not come back was deleted at its source, which is a normal
 * thing to happen to a post: the caller skips it rather than showing a hole.
 * It is asked for once more first, because the alternative reading — that the
 * record is a moment too young to read — is the one that gets the answer wrong
 * for the person who just wrote it (see `SECOND_CHANCE_MS`).
 */
export function useClassroomPostViews(uris: string[]) {
  const agent = useAgent()
  const stableUris = useMemo(() => Array.from(new Set(uris)).sort(), [uris])

  return useQuery<AppBskyFeedDefs.PostView[]>({
    queryKey: RQKEY(stableUris),
    enabled: stableUris.length > 0,
    queryFn: async () => {
      const posts = await fetchPosts(agent, stableUris)
      const found = new Set(posts.map(post => post.uri))
      const missing = stableUris.filter(uri => !found.has(uri))
      if (missing.length === 0) return posts
      // Asked once more, after a breath, before the page decides these are gone.
      await wait(SECOND_CHANCE_MS)
      return [...posts, ...(await fetchPosts(agent, missing))]
    },
    retry: false,
    staleTime: 30_000,
  })
}

/** The fetched posts, keyed by URI, for looking one up while rendering a list. */
export function usePostViewsByUri(
  posts: AppBskyFeedDefs.PostView[] | undefined,
): Map<string, AppBskyFeedDefs.PostView> {
  return useMemo(() => {
    const map = new Map<string, AppBskyFeedDefs.PostView>()
    for (const post of posts ?? []) map.set(post.uri, post)
    return map
  }, [posts])
}
